import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

export type StatusType =
  | 'active'
  | 'inactive'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'completed'
  | 'cancelled';

/**
 * StatusBadge Component - Display status with appropriate colors
 *
 * @example
 * <app-atom-status-badge [status]="1" [labels]="{ 1: 'Active', 2: 'Inactive' }">
 * </app-atom-status-badge>
 */
@Component({
  selector: 'app-atom-status-badge',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './status-badge.component.html',
  styleUrls: ['./status-badge.component.scss'],
})
export class StatusBadgeComponent {
  // Inputs
  status = input<number | string | null>(null);
  labels = input<Record<number | string, string>>({});
  type = input<StatusType | null>(null);
  customClass = input<string>('');

  /**
   * Computed display text based on status and labels
   */
  displayText = computed(() => {
    const statusVal = this.status();
    const labelsMap = this.labels();

    if (statusVal !== null && labelsMap[statusVal]) {
      return labelsMap[statusVal];
    }

    // Default status text mapping
    const defaultMap: Record<number, string> = {
      1: 'Active',
      2: 'Inactive',
      0: 'Inactive',
    };

    if (statusVal !== null && defaultMap[statusVal as number]) {
      return defaultMap[statusVal as number];
    }

    return statusVal?.toString() || 'Unknown';
  });

  /**
   * Computed badge classes based on status
   */
  badgeClasses = computed(() => {
    const statusVal = this.status();
    const typeVal = this.type();
    const customClassVal = this.customClass();

    if (customClassVal) {
      return customClassVal;
    }

    // Type-based styling
    if (typeVal) {
      const typeMap: Record<StatusType, string> = {
        active: 'bg-green-100 text-green-800',
        inactive: 'bg-gray-100 text-gray-800',
        pending: 'bg-yellow-100 text-yellow-800',
        approved: 'bg-blue-100 text-blue-800',
        rejected: 'bg-red-100 text-red-800',
        completed: 'bg-green-100 text-green-800',
        cancelled: 'bg-gray-100 text-gray-800',
      };
      return typeMap[typeVal];
    }

    // Numeric status-based styling (default: 1=active, 2/0=inactive)
    if (statusVal === 1) {
      return 'bg-green-100 text-green-800';
    } else if (statusVal === 2 || statusVal === 0) {
      return 'bg-gray-100 text-gray-800';
    }

    // Default
    return 'bg-gray-100 text-gray-800';
  });
}
